/**
 * My profile update - pure state transition logic.
 *
 * Elm-ish architecture: Update is a pure function (no side effects).
 * Given a model and a message, returns a new model.
 *
 * S018 authority: Figma node 39:4695
 *
 * Required invariants:
 * 1. accountId is non-empty but never rendered
 * 2. displayName is non-empty after trimming
 * 3. age is a finite positive integer
 * 4. city is non-empty after trimming
 * 5. avatarUrl satisfies safe-asset rules
 * 6. completionPercent is integer in [0, 100]
 * 7. ready always contains one valid profile
 * 8. Raw adapter exceptions not stored as rendered copy
 * 9. Stale load completions are ignored
 * 10. Logout failure preserves current authenticated profile
 * 11. Duplicate logout submissions rejected
 * 12. Model contains no JSX, router instances, or browser globals
 * 13. LogoutErrorCleared transitions failed -> idle without requestId
 */
import type { MyProfileModel } from './model';
import type { MyProfileMsg } from './messages';
import type { MyProfileSummary } from './port';

/**
 * Update the model based on a message.
 * This is a pure function - no side effects.
 *
 * @param model - Current model state
 * @param msg - Message to process
 * @returns Updated model state
 */
export function updateMyProfile(
  model: MyProfileModel,
  msg: MyProfileMsg
): MyProfileModel {
  switch (msg.type) {
    case 'LoadStarted':
      // Idempotent: if already in this generation, return same model
      if (model.status === 'loading' && model.activeLoadId === msg.loadId) {
        return model;
      }
      return {
        status: 'loading',
        activeLoadId: msg.loadId,
      };

    case 'LoadSucceeded': {
      // Ignore stale completions
      if (model.status !== 'loading' || msg.loadId !== model.activeLoadId) {
        return model;
      }
      return {
        status: 'ready',
        activeLoadId: msg.loadId,
        profile: msg.profile,
        logout: { status: 'idle' },
      };
    }

    case 'LoadFailed': {
      // Ignore stale failures
      if (model.status !== 'loading' || msg.loadId !== model.activeLoadId) {
        return model;
      }
      return {
        status: 'load_failed',
        activeLoadId: msg.loadId,
      };
    }

    case 'RetryRequested': {
      // Generate new load ID for retry
      const newLoadId = model.activeLoadId + 1;
      return {
        status: 'loading',
        activeLoadId: newLoadId,
      };
    }

    case 'LogoutRequested': {
      // Only allow logout from ready/idle state
      if (model.status !== 'ready') {
        return model;
      }
      // Reject duplicate logout requests
      if (model.logout.status === 'submitting') {
        return model;
      }
      return {
        ...model,
        logout: { status: 'submitting', requestId: msg.requestId },
      };
    }

    case 'LogoutSucceeded': {
      // Only process if this request is the current one
      if (model.status !== 'ready' || model.logout.status !== 'submitting') {
        return model;
      }
      if (model.logout.requestId !== msg.requestId) {
        return model;
      }
      // Preserve profile during logout transition (for potential re-auth)
      return {
        ...model,
        logout: { status: 'idle' },
      };
    }

    case 'LogoutFailed': {
      // Only process if this request is the current one
      if (model.status !== 'ready' || model.logout.status !== 'submitting') {
        return model;
      }
      if (model.logout.requestId !== msg.requestId) {
        return model;
      }
      // Restore actionable ready state - preserve profile and session
      // Note: failed state has no requestId - it was already consumed
      return {
        ...model,
        logout: { status: 'failed' },
      };
    }

    case 'LogoutErrorCleared': {
      // Only clear error from ready/failed state
      if (model.status !== 'ready' || model.logout.status !== 'failed') {
        return model;
      }
      // Simply restore idle - no requestId needed for error clear
      return {
        ...model,
        logout: { status: 'idle' },
      };
    }

    default:
      // Exhaustive match - should never reach here
      return model;
  }
}

/**
 * Create a ready model with a validated profile.
 */
export function createReadyModel(
  profile: MyProfileSummary,
  loadId: number
): MyProfileModel {
  return {
    status: 'ready',
    activeLoadId: loadId,
    profile,
    logout: { status: 'idle' },
  };
}
